<script lang="ts" setup>
import { computed } from "vue";

import ArticleContent from "@/components/article/ArticleContent.vue";
import ArticleContentCollapser from "@/components/article/ArticleContentCollapser.vue";
import ContentsWithLinks from "@/components/article/politiken/ContentsWithLinks.vue";
import { type PolitikenForeignArticle } from "@/types/article-formats/politiken";

const props = defineProps<{
  article: PolitikenForeignArticle;
  isCollapsible: boolean;
}>();

const hasMeanings = computed<boolean>(() => props.article.meanings.length > 0);
const hasEtymologies = computed<boolean>(() => props.article.etymologies.length > 0);

const isLast = (index: number, array: object[]) => index === array.length - 1;
</script>

<template>
  <!-- eslint-disable vue/no-v-html -->
  <ArticleContent>
    <div class="article-content__header article-header">
      <div class="article-header__content">
        <span
          v-if="article.word.referenceMark"
          class="article-header__element article-header__element--unchained"
        >
          {{ article.word.referenceMark }}
        </span>

        <span
          v-if="article.word.pronoun"
          class="article-header__element article-header__element--unchained article-header__element--light"
        >
          {{ article.word.pronoun }}
        </span>

        <h5
          class="article-header__word article-header__element"
          :class="{
            'article-header__element--unchained':
              !article.word.referenceMark && !article.word.pronoun,
            'article-header__element--chained': article.word.referenceMark || article.word.pronoun
          }"
        >
          {{ article.word.lemmaOne }}
        </h5>

        <sup v-if="article.word.hnr" class="pol-header-sup">{{ article.word.hnr }}</sup>

        <span
          v-if="article.word.registeredTrademark"
          class="article-header__element article-header__element--chained"
        >
          {{ article.word.registeredTrademark }}
        </span>

        <span v-if="article.word.phoneticScriptOne" class="article-header__element">
          [<span
            class="article-header__element article-header__element--unchained article-header__element--italic"
            v-html="article.word.phoneticScriptOne"
          ></span
          >]
        </span>

        <span
          v-if="props.article.word.wordClass"
          class="article-header__element article-header__element--light article-header__element--italic"
        >
          {{ props.article.word.wordClass }}
        </span>

        <span
          v-if="props.article.word.flexionOne"
          class="article-header__element article-header__element--light article-header__element--italic"
          v-html="props.article.word.flexionOne"
        ></span>

        <template v-if="article.word.lemmaTwo">
          <span class="article-header__element article-header__element--comment">eller</span>
          <h5
            class="article-header__word article-header__element"
            v-html="article.word.lemmaTwo"
          ></h5>

          <span
            v-if="props.article.word.flexionTwo"
            class="article-header__element article-header__element--light article-header__element--italic"
            v-html="props.article.word.flexionTwo"
          ></span>
        </template>

        <span v-if="props.article.word.abbreviation" class="article-header__element">
          forkortes:
          <span
            class="article-header__element article-header__element--unchained article-header__element--prominent"
            v-html="props.article.word.abbreviation"
          ></span>
        </span>

        <span
          v-if="article.word.reference.length > 0"
          class="article-header__element article-header__element--chained"
        >
          <ContentsWithLinks
            v-for="(reference, index) in article.word.reference"
            :key="index"
            :content="reference"
            link-class="article-content__link"
          />
        </span>
      </div>
    </div>

    <ArticleContentCollapser
      v-if="hasMeanings || hasEtymologies"
      :is-collapsible="props.isCollapsible"
    >
      <div class="article-content__body-container article-content__body-container--reading-lines">
        <div class="indentation-container">
          <template v-if="hasMeanings">
            <div v-for="(meaning, meaningIndex) in article.meanings" :key="meaningIndex">
              <div class="indentation-container">
                <span
                  v-if="meaning.bnrType === 'BNR'"
                  class="article-list-bullet article-list-bullet--primary article-list-bullet--prominent"
                >
                  {{ meaning.bnr }}.
                </span>

                <div
                  :class="{
                    'indentation-container':
                      meaning.bnrType === 'ARROW' || meaning.bnrType === 'BULLET'
                  }"
                >
                  <span v-if="meaning.bnrType === 'ARROW'" class="article-list-bullet">
                    &#9657;
                  </span>
                  <span v-if="meaning.bnrType === 'BULLET'" class="article-list-bullet">
                    &#9679;
                  </span>

                  <span
                    v-if="meaning.sublemma"
                    class="article-content__text article-content__text--prominent pr-2"
                    v-html="meaning.sublemma"
                  ></span>

                  <span
                    v-if="meaning.fComment"
                    class="article-content__text article-content__text--light pr-2"
                    >(<span v-html="meaning.fComment"></span>)</span
                  >

                  <span
                    v-if="meaning.rComment"
                    class="article-content__text article-content__text--light pr-2"
                    >({{ meaning.rComment }}):</span
                  >

                  <ContentsWithLinks
                    v-for="(definition, definitionIndex) in meaning.definition"
                    :key="definitionIndex"
                    :content="definition"
                    text-class="article-content__text"
                    link-class="article-content__link"
                  />

                  <div v-if="meaning.synonym" class="article-content__politiken-grid">
                    <div class="article-content__text article-content__politiken-grid-label">
                      Synonym:
                    </div>
                    <div
                      class="article-content__politiken-grid-content article-content__text article-content__text--primary"
                    >
                      <span
                        class="article-content__text article-content__text--primary"
                        v-html="meaning.synonym"
                      ></span>
                    </div>
                  </div>

                  <div v-if="meaning.antonym" class="article-content__politiken-grid">
                    <div class="article-content__text article-content__politiken-grid-label">
                      Antonym:
                    </div>
                    <div
                      class="article-content__politiken-grid-content article-content__text article-content__text--primary"
                    >
                      <span
                        class="article-content__text article-content__text--primary"
                        v-html="meaning.antonym"
                      ></span>
                    </div>
                  </div>

                  <div
                    v-if="meaning.sublemmaSmsOne.length > 0 || meaning.sublemmaSmsTwo.length > 0"
                    class="article-content__politiken-grid"
                  >
                    <div class="article-content__text article-content__politiken-grid-label">
                      Sammensatte ord:
                    </div>
                    <div
                      class="article-content__politiken-grid-content article-content__text article-content__text--primary"
                    >
                      <ContentsWithLinks
                        v-for="(sublemmaSms, sublemmaSmsIndex) in [
                          ...meaning.sublemmaSmsOne,
                          ...meaning.sublemmaSmsTwo
                        ]"
                        :key="sublemmaSmsIndex"
                        :content="sublemmaSms"
                        text-class="article-content__text article-content__text--primary"
                        link-class="article-content__link"
                      />
                    </div>
                  </div>

                  <div v-if="meaning.derivation.length > 0" class="article-content__politiken-grid">
                    <div class="article-content__text article-content__politiken-grid-label">
                      Afledning:
                    </div>
                    <div
                      class="article-content__politiken-grid-content article-content__text article-content__text--primary"
                    >
                      <ContentsWithLinks
                        v-for="(derivation, derivationIndex) in meaning.derivation"
                        :key="derivationIndex"
                        :content="derivation"
                        text-class="article-content__text article-content__text--primary"
                        link-class="article-content__link"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>

          <template v-if="hasEtymologies">
            <div v-if="hasMeanings" class="article-content__spacer"></div>

            <div class="article-content__politiken-grid">
              <div class="article-content__text article-content__politiken-grid-label">
                Etymologi:
              </div>
              <div
                class="article-content__politiken-grid-content article-content__text article-content__text--primary"
              >
                <div v-for="(etymologi, index) in article.etymologies" :key="index">
                  <span v-html="etymologi.origin"></span>

                  <div
                    v-if="!isLast(index, article.etymologies)"
                    class="article-content__spacer"
                  ></div>
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>
    </ArticleContentCollapser>
  </ArticleContent>
</template>
